import { StageEnum } from '@app/types';
import {
  BaseFoodEmissionFactors,
  EmissionFactor,
  FoodActivityRetrieveUpdateOverrideEmissionFactors,
  FoodOperationActivityDetailOverrideEmissionFactors,
  FPPTEmissionFactors,
  FPPTEmissionsPerKg,
} from '@shared/api/types';
import { EmissionFactorsSchema, EmissionsFactorSchema } from './schema';

// Helper function to get the effective emission factor for a stage
export const getEffectiveEmissionFactor = (
  stage: StageEnum,
  baseFactors: BaseFoodEmissionFactors,
  overrideFactors: FPPTEmissionFactors
): EmissionFactor => {
  // If override exists for this stage, use it; otherwise use base
  return overrideFactors[stage]?.value != null
    ? overrideFactors[stage]!
    : baseFactors[stage];
};

// Helper function to determine if a stage has override values
export const hasOverride = (
  stage: StageEnum,
  overrideFactors: FPPTEmissionFactors
): boolean => {
  return overrideFactors?.[stage]?.value != null;
};

export const getEmissionFactors = (
  stages: StageEnum[],
  baseEmissionFactors: BaseFoodEmissionFactors,
  overrideEmissionFactors:
    | FoodOperationActivityDetailOverrideEmissionFactors
    | FoodActivityRetrieveUpdateOverrideEmissionFactors
    | FPPTEmissionFactors
) => {
  return stages.reduce(
    (acc, stage) => {
      const effectiveFactor = getEffectiveEmissionFactor(
        stage,
        baseEmissionFactors,
        overrideEmissionFactors!
      );

      const isOverride = hasOverride(stage, overrideEmissionFactors!);

      acc[stage] = {
        method: isOverride ? 'custom' : 'standard',
        value: effectiveFactor.value!,
        source: effectiveFactor.source!,
      };
      return acc;
    },
    {} as Record<StageEnum, EmissionsFactorSchema>
  );
};

export const emptyEmissionFactors = {
  farming: {
    value: null,
    source: null,
  },
  processing: {
    value: null,
    source: null,
  },
  packaging: {
    value: null,
    source: null,
  },
  transport: {
    value: null,
    source: null,
  },
};

export function formatEmissionFactors(
  emissionFactors: FPPTEmissionsPerKg
): EmissionFactorsSchema {
  return {
    farming: {
      value: emissionFactors.farmingEmissionsPerKg!,
      source: '',
    },
    processing: {
      value: emissionFactors.processingEmissionsPerKg!,
      source: '',
    },
    packaging: {
      value: emissionFactors.packagingEmissionsPerKg!,
      source: '',
    },
    transport: {
      value: emissionFactors.transportEmissionsPerKg!,
      source: '',
    },
  };
}
